import React, {useCallback, useEffect, useState} from 'react'
import {useDispatch} from 'react-redux'
import Autorizado from '../../../components/Autorizado/Autorizado'
import ModalConfirm from '../../../components/Modals/Generales/ModalConfirm'
import {ROLES_MIGRACION_NORMATIVA} from '../../../constants/roles'
import {AlistamientoNormativa} from '../../../interfaces/normativa.interface'
import {normativaCambiarVersion} from '../../../actions/normativa/normativa.actions'
import {recargarCatalogosDeLaNormativa} from '../../../actions/catalogos.actions'
import {generalOpenModalError, generalOpenModalInfo} from '../../../actions/general.actions'
import {
  VERSION_NORMATIVA_V1,
  VERSION_NORMATIVA_V2,
  nombreVersionNormativa,
} from '../../../utils/normativa.utils'
import AvisoVerificacionDatos from '../../../components/AvisoVerificacionDatos/AvisoVerificacionDatos'
import {verificacionGetPendientes} from '../../../actions/normativa/verificacionDatos.actions'
import {
  TIPO_ENTIDAD_EMPRESA,
  VerificacionDato,
} from '../../../interfaces/verificacion-datos.interface'
import {motivoCodigosEstablecimiento} from '../../../utils/verificacion-datos.utils'

const LARGO_MAXIMO_OBSERVACION = 500

const DETALLE_PASO_A_VERSION_DOS =
  'Los documentos que emita a partir de este momento se generarán con la versión 2 y con las series del formato nuevo. Confirme que ya realizó los trámites y las pruebas ante el Ministerio de Hacienda, porque los documentos que emita tienen efecto fiscal. ¿Desea continuar?'

const DETALLE_REGRESO_A_VERSION_UNO =
  'Los documentos que emita a partir de este momento se generarán de nuevo con la versión 1. Los documentos que ya emitió con la versión 2 conservan esa versión y no se modifican. ¿Desea continuar?'

const AVISO_SESION =
  'La plataforma ya trabaja con esa versión en esta misma sesión. Si tiene otras pestañas abiertas, actualícelas.'

const INTRODUCCION_VERIFICACION =
  'Puede pasar a la versión 2 y podrá invalidar sus documentos: mientras no registre estos códigos, la plataforma los deriva de la serie del documento. Aun así le conviene registrarlos, porque los que valen ante el Ministerio de Hacienda son los que el Ministerio le asigna.'

interface PropsInterruptorVersion {
  alistamiento: AlistamientoNormativa
  onVersionCambiada: () => void
}

const InterruptorVersion = ({alistamiento, onVersionCambiada}: PropsInterruptorVersion) => {
  const dispatch = useDispatch()
  const [observacion, setObservacion] = useState('')
  const [confirmando, setConfirmando] = useState(false)
  const [verificaciones, setVerificaciones] = useState<Array<VerificacionDato>>([])

  const consultarVerificaciones = useCallback(async () => {
    const pendientes: any = await dispatch(verificacionGetPendientes(TIPO_ENTIDAD_EMPRESA))
    setVerificaciones(Array.isArray(pendientes) ? pendientes : [])
  }, [dispatch])

  useEffect(() => {
    consultarVerificaciones()
  }, [consultarVerificaciones])

  const versionActual = `${alistamiento?.versionactual ?? ''}`.trim()
  const enVersionDos = versionActual === VERSION_NORMATIVA_V2
  const versionDestino = enVersionDos ? VERSION_NORMATIVA_V1 : VERSION_NORMATIVA_V2
  const habilitado = enVersionDos || alistamiento?.puedeMigrarVersionDos === true

  const etiquetaBoton = enVersionDos ? 'Regresar a la versión 1' : 'Pasar a la versión 2'

  const motivoCodigos = motivoCodigosEstablecimiento(verificaciones)

  const confirmarCambio = async () => {
    setConfirmando(false)
    const resultado: any = await dispatch(normativaCambiarVersion(versionDestino, observacion))

    if (resultado?.exitoso) {
      setObservacion('')
      await dispatch(recargarCatalogosDeLaNormativa() as any)
      dispatch(generalOpenModalInfo(`${resultado.mensaje} ${AVISO_SESION}`))
    } else {
      dispatch(generalOpenModalError(resultado?.mensaje ?? 'No se logró cambiar la versión.'))
    }

    onVersionCambiada()
    await consultarVerificaciones()
  }

  return (
    <Autorizado roles={ROLES_MIGRACION_NORMATIVA}>
      <div className='normativa-bloque'>
        <h3 className='card-title l'>Cambio de versión</h3>
        <p className='normativa-texto m'>
          Su empresa se encuentra en la {nombreVersionNormativa(versionActual)}. El cambio de
          versión lo decide su empresa y no es definitivo: puede regresar a la versión anterior
          cuando lo necesite.
        </p>

        {!enVersionDos && motivoCodigos && (
          <div style={{marginBottom: '16px'}}>
            <AvisoVerificacionDatos
              verificaciones={[motivoCodigos]}
              titulo='Le faltan los códigos de establecimiento del Ministerio de Hacienda'
              introduccion={INTRODUCCION_VERIFICACION}
            />
          </div>
        )}

        {!enVersionDos && !habilitado && (
          <div className='normativa-aviso'>
            <p className='normativa-aviso-titulo m'>Todavía no puede pasar a la versión 2</p>
            <p className='m'>
              Su empresa tiene requisitos pendientes en el alistamiento. Resuelva los requisitos
              marcados como pendientes en esta pantalla y vuelva a revisar el alistamiento para
              habilitar el cambio.
            </p>
          </div>
        )}

        <div className='label-input'>
          <label className='field-label m'>Observación (opcional)</label>
          <textarea
            value={observacion}
            onChange={(evento) => setObservacion(evento.target.value)}
            placeholder='Indique el motivo del cambio de versión para dejarlo registrado en la bitácora.'
            maxLength={LARGO_MAXIMO_OBSERVACION}
            rows={3}
            className='m text-field w-input'
            style={{
              height: 'auto',
              minHeight: '90px',
              resize: 'vertical',
              paddingTop: '12px',
              lineHeight: '1.5',
            }}
          />
          <p style={{fontSize: '11px', color: 'rgba(27, 27, 27, 0.4)', marginTop: '4px'}}>
            {`${observacion.length} de ${LARGO_MAXIMO_OBSERVACION} caracteres`}
          </p>
        </div>

        <div className='normativa-acciones'>
          <button
            className='m button w-inline-block'
            onClick={() => setConfirmando(true)}
            disabled={!habilitado}
          >
            <div>{etiquetaBoton}</div>
          </button>
        </div>
      </div>

      {confirmando && (
        <ModalConfirm
          title={etiquetaBoton}
          detail={enVersionDos ? DETALLE_REGRESO_A_VERSION_UNO : DETALLE_PASO_A_VERSION_DOS}
          messageAction='Sí, cambiar la versión'
          messageClose='No, cancelar'
          actionConfirm={confirmarCambio}
          closeModalConfirm={() => setConfirmando(false)}
        />
      )}
    </Autorizado>
  )
}

export default InterruptorVersion
